import { useEffect, useMemo, useState } from 'react';
import type { IField, ILookup } from '../../services/metadataService';
import { getReferenceValues } from '../../services/referenceService';

const REFERENCE_PAGE_SIZE = 1000;

// Resolves lookup values for fields from /p/referenceValues, falling back to the given lookups.
export const useReferenceLookups = (
  fields: IField[] | undefined,
  lookups: ILookup[] | undefined,
  enabled = true
): ILookup[] => {
  const [refValues, setRefValues] = useState<Record<string, ILookup['values']>>(
    {}
  );

  // AutoComplete fields query their own endpoint, so only list-style fields need values here.
  const refNamesKey = useMemo(
    () =>
      [
        ...new Set(
          (fields ?? [])
            .filter((f) => f.lookupList && f.fieldType !== 'AutoComplete')
            .map((f) => f.lookupList as string)
        ),
      ]
        .sort()
        .join('|'),
    [fields]
  );

  useEffect(() => {
    if (!enabled || !refNamesKey) return;
    const missing = refNamesKey
      .split('|')
      .filter((name) => !(name in refValues));
    if (!missing.length) return;
    const abortController = new AbortController();
    Promise.all(
      missing.map(async (name) => {
        try {
          const result = await getReferenceValues(
            abortController,
            REFERENCE_PAGE_SIZE,
            1,
            encodeURIComponent(name)
          );
          const items = (result?.items ?? []) as unknown as {
            key: string;
            value: string;
          }[];
          return [
            name,
            items.map((i) => ({ key: `${i.key}`, value: i.value })),
          ] as const;
        } catch {
          return [name, [] as ILookup['values']] as const;
        }
      })
    ).then((entries) => {
      if (abortController.signal.aborted) return;
      setRefValues((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refNamesKey]);

  return useMemo(() => {
    const fetched = Object.entries(refValues)
      .filter(([, values]) => values.length)
      .map(([name, values]) => ({ name, values }));
    const fetchedNames = new Set(fetched.map((l) => l.name));
    return [
      ...(lookups ?? []).filter((l) => !fetchedNames.has(l.name)),
      ...fetched,
    ];
  }, [lookups, refValues]);
};
